import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { usePageTitle } from "context/PageTitleContext";
import { PERMISSIONS } from "auth/permissionCodes";
import { usePermissions } from "hooks/usePermissions";
import {
  createDocumentType,
  deleteDocumentType,
  getDocumentTypes,
  updateDocumentType,
} from "../../services/documentType.service";
import DocumentTypeFormDialog from "./DocumentTypeFormDialog";
import DeleteDocumentTypeDialog from "./DeleteDocumentTypeDialog";
import DataTable from "components/common/tables/DataTable";
import { AppButton } from "components/common/buttons";
import { AppSelect } from "components/common/inputs";
import { AppFilterBar } from "components/common/filters";

const ALL_DOCUMENT_CATEGORIES = "__ALL_DOCUMENT_CATEGORIES__";

const normalizeDocumentType = (item) => ({
  Id: item?.Id ?? item?.id,
  Name: item?.Name ?? item?.name ?? "",
  DocumentCategory: item?.DocumentCategory ?? item?.documentCategory ?? "",
  Note: item?.Note ?? item?.note ?? "",
});

const getResponseMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.message ||
  fallback;

const DocumentTypeManagement = () => {
  //#region State & variables
  const { setPageTitle } = usePageTitle();
  const permissions = usePermissions();
  const canCreate = permissions.can(PERMISSIONS.SYSTEM.DOCUMENT_TYPE_CREATE);
  const canUpdate = permissions.can(PERMISSIONS.SYSTEM.DOCUMENT_TYPE_UPDATE);
  const canDelete = permissions.can(PERMISSIONS.SYSTEM.DOCUMENT_TYPE_DELETE);
  const canMutate = canUpdate || canDelete;
  const [data, setData] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(ALL_DOCUMENT_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  //#endregion

  //#region Effects & handlers
  useEffect(() => {
    setPageTitle("Quản lý danh mục tài liệu");
  }, [setPageTitle]);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadCategoryOptions = useCallback(async () => {
    const response = await getDocumentTypes({
      pageSize: 1000,
      pageNumber: 1,
      sortColumn: "DocumentCategory",
      sortDirection: "ASC",
    });

    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.Data)
        ? response.Data
        : [];

    const options = Array.from(
      new Set(
        rows
          .map((item) => normalizeDocumentType(item).DocumentCategory)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, "vi"));

    setCategoryOptions(options);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDocumentTypes({
        documentCategory:
          selectedCategory === ALL_DOCUMENT_CATEGORIES ? undefined : selectedCategory,
        pageSize: 1000,
        pageNumber: 1,
        sortColumn: "DocumentCategory",
        sortDirection: "ASC",
      });

      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.Data)
          ? response.Data
          : [];

      setData(rows.map(normalizeDocumentType));
    } catch (error) {
      setData([]);
      showSnackbar(getResponseMessage(error, "Lỗi khi tải danh sách loại tài liệu"), "error");
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadCategoryOptions().catch((error) => {
      showSnackbar(getResponseMessage(error, "Lỗi khi tải nhóm tài liệu"), "error");
    });
  }, [loadCategoryOptions, showSnackbar]);

  const reloadAfterMutation = useCallback(async () => {
    await Promise.all([loadData(), loadCategoryOptions()]);
  }, [loadCategoryOptions, loadData]);

  const handleCreate = async (payload) => {
    if (!canCreate || saving) return;
    setSaving(true);
    try {
      await createDocumentType(payload);
      setAddOpen(false);
      showSnackbar("Thêm loại tài liệu thành công", "success");
      await reloadAfterMutation();
    } catch (error) {
      showSnackbar(getResponseMessage(error, "Lỗi khi thêm loại tài liệu"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    if (!canUpdate || !editTarget?.Id || saving) return;

    setSaving(true);
    try {
      await updateDocumentType(editTarget.Id, payload);
      setEditTarget(null);
      showSnackbar("Cập nhật loại tài liệu thành công", "success");
      await reloadAfterMutation();
    } catch (error) {
      showSnackbar(getResponseMessage(error, "Lỗi khi cập nhật loại tài liệu"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !deleteTarget?.Id || saving) return;

    setSaving(true);
    try {
      await deleteDocumentType(deleteTarget.Id);
      setDeleteTarget(null);
      showSnackbar("Xóa loại tài liệu thành công", "success");
      await reloadAfterMutation();
    } catch (error) {
      showSnackbar(getResponseMessage(error, "Lỗi khi xóa loại tài liệu"), "error");
    } finally {
      setSaving(false);
    }
  };

  const documentCategoryEditOptions = useMemo(() => {
    const options = new Set(categoryOptions);
    if (editTarget?.DocumentCategory) options.add(editTarget.DocumentCategory);
    data.forEach((item) => {
      if (item.DocumentCategory) options.add(item.DocumentCategory);
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b, "vi"));
  }, [categoryOptions, data, editTarget]);
  //#endregion

  //#region Column setup
  const columns = useMemo(
    () => [
      { accessorKey: "Id", header: "Mã loại tài liệu", size: 70 },
      { accessorKey: "Name", header: "Tên loại tài liệu", size: 260 },
      { accessorKey: "DocumentCategory", header: "Nhóm tài liệu", size: 220 },
      {
        accessorKey: "Note",
        header: "Ghi chú",
        size: 360,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
    ],
    []
  );
  //#endregion

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 64px)", backgroundColor: "#f9fafb" }}>
      <Stack spacing={2} sx={{ height: "100%" }}>

        {/* <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            backgroundColor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 280 } }}>
              <InputLabel>Nhóm tài liệu</InputLabel>
              <Select
                value={selectedCategory}
                label="Nhóm tài liệu"
                onChange={(event) => setSelectedCategory(event.target.value)}
              >
                <MenuItem value="">Tất cả nhóm tài liệu</MenuItem>
                {categoryOptions.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
                disableElevation
                sx={{ whiteSpace: "nowrap" }}
              >
                Thêm mới
              </Button>
            )}
          </Stack>
        </Paper> */}

        <AppFilterBar icon={false}>
          <AppSelect
            label="Nhóm tài liệu"
            value={selectedCategory}
            options={[
              { label: "Tất cả nhóm tài liệu", value: ALL_DOCUMENT_CATEGORIES },
              ...categoryOptions.map(x => ({
                label: x,
                value: x,
              })),
            ]}
            onChange={(value) => setSelectedCategory(value)}
            sx={{ width: 280 }}
          />

          <Box sx={{ flex: 1 }} />

          {canCreate && (
            <AppButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
              disabled={saving}
              disableElevation
              sx={{ whiteSpace: "nowrap" }}
            >
              Thêm mới
            </AppButton>
          )}
        </AppFilterBar>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataTable
            columns={columns}
            data={data}
            enableEditing={false}
            enableRowActions={canMutate}
            getRowId={(row) => String(row.Id)}
            state={{ isLoading: loading, showProgressBars: saving }}
            initialState={{
              columnVisibility: {
                Id: false,
              },
            }}
            renderRowActions={({ row }) => (
              <Stack direction="row" spacing={0.5} justifyContent="center">
                {canUpdate && (
                  <Tooltip title="Sửa">
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="Sửa"
                      disabled={saving}
                      onClick={() => setEditTarget(row.original)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip title="Xóa">
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Xóa"
                      disabled={saving}
                      onClick={() => setDeleteTarget(row.original)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
          />
        </Box>
      </Stack>

      <DocumentTypeFormDialog
        open={addOpen || Boolean(editTarget)}
        documentType={editTarget}
        onClose={() => {
          if (saving) return;
          setAddOpen(false);
          setEditTarget(null);
        }}
        onSubmit={editTarget ? handleUpdate : handleCreate}
        documentCategoryOptions={documentCategoryEditOptions}
        defaultDocumentCategory={
          selectedCategory === ALL_DOCUMENT_CATEGORIES ? "" : selectedCategory
        }
        submitting={saving}
      />

      <DeleteDocumentTypeDialog
        open={Boolean(deleteTarget)}
        documentType={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        submitting={saving}
      />

      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />
    </Box>
  );
};

export default DocumentTypeManagement;
